import React, { useState, useEffect, useCallback } from 'react';
import { theme, statusTheme } from './theme';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { PROFESSIONS, SIGNAL_BADGES, FL_COUNTIES } from './constants';
import PublicListings from './PublicListings';
import LandingPage from './LandingPage';
import TermsPage from './TermsPage';
import GuidePage from './GuidePage';
import MonthlyReport from './MonthlyReport';
import { initAnalytics, trackPage } from './analytics';

initAnalytics();

// Fires a page-view on every route change (SPA navigation).
function RouteAnalytics() {
  const location = useLocation();
  useEffect(() => { trackPage(location.pathname); }, [location.pathname]);
  return null;
}

const API = process.env.REACT_APP_API_URL || 'https://api.leadengine.amanar.ai';

// ─────────────────────────────────────────────────────────────────────────
// API helper
// ─────────────────────────────────────────────────────────────────────────
class UnauthorizedError extends Error {
  constructor(msg) { super(msg); this.name = 'UnauthorizedError'; }
}

async function api(path, { token, ...opts } = {}) {
  const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API}${path}`, { ...opts, headers, credentials: 'include' });
  if (res.status === 401) throw new UnauthorizedError('Session expired');
  return res;
}

// Live viewport-width flag. The lead table is 1000px+ wide, so on phones we
// swap it for a card layout instead of forcing a horizontal scroll.
const useIsMobile = (breakpoint = 720) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [breakpoint]);
  return isMobile;
};

const STATUSES = ['new', 'contacted', 'qualified', 'won', 'lost', 'not_a_fit'];
const FILTERS = [
  { key: 'all', label: 'All leads' },
  { key: 'available', label: 'Available' },
  { key: 'mine', label: 'My leads' },
  { key: 'due', label: 'Due today' },
  { key: 'active', label: 'My active' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'not_a_fit', label: 'Not a fit' },
  { key: 'won', label: 'Won' },
];
// Which pool counter (X-Count-* response headers) each chip renders. The
// three counters recount against the whole database on every filter change,
// so the list never reads as smaller than the data actually is.
const FILTER_COUNTS = { all: 'total', available: 'available', mine: 'mine', due: 'due' };

const PAGE_SIZES = [50, 100, 250, 500];

// ─────────────────────────────────────────────────────────────────────────
// Auth screen
// ─────────────────────────────────────────────────────────────────────────
const AuthScreen = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [msg, setMsg] = useState({ text: '', color: theme.text });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSignUp && !acceptedTerms) {
      setMsg({ text: 'Please accept the Terms & Acceptable Use Policy to continue.', color: theme.danger });
      return;
    }
    try {
      const res = await api(`/api/v1/auth/${isSignUp ? 'register' : 'login'}`, {
        method: 'POST',
        body: JSON.stringify({
          email, password, first_name: email.split('@')[0],
          ...(isSignUp ? { accepted_terms: acceptedTerms } : {}),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        if (isSignUp) {
          setMsg({ text: 'Account created. Sign in to continue.', color: theme.accent });
          setIsSignUp(false);
        } else {
          onLoginSuccess(data.data.access_token);
        }
      } else {
        setMsg({ text: data.detail || 'Authentication failed.', color: theme.danger });
      }
    } catch {
      setMsg({ text: 'Cannot reach the server. Is the backend running?', color: theme.danger });
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px', marginBottom: '14px', borderRadius: '6px',
    border: `1px solid ${theme.border}`, backgroundColor: theme.bg, color: theme.heading,
    boxSizing: 'border-box', fontSize: '0.95rem',
  };

  return (
    <div style={{ backgroundColor: theme.bg, minHeight: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: '20px', boxSizing: 'border-box',
      fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <form onSubmit={handleSubmit} style={{
        backgroundColor: theme.surface, padding: '32px', borderRadius: '12px',
        border: `1px solid ${theme.border}`, width: '100%', maxWidth: '400px',
        boxSizing: 'border-box', boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
      }}>
        <h1 style={{ color: theme.heading, fontSize: '2rem', margin: '0 0 4px', letterSpacing: '2px' }}>AMANAR</h1>
        <p style={{ color: theme.primary, margin: '0 0 28px', fontWeight: 600, fontSize: '0.9rem' }}>LEAD ENGINE</p>
        <input style={inputStyle} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <input style={inputStyle} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength="8" />
        {isSignUp && (
          <label style={{ display: 'flex', gap: '9px', alignItems: 'flex-start', margin: '2px 0 16px',
            color: theme.textMute, fontSize: '0.8rem', lineHeight: 1.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)}
              style={{ marginTop: '2px', cursor: 'pointer', flexShrink: 0 }} />
            <span>
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noreferrer" style={{ color: theme.primary }}>
                Terms &amp; Acceptable Use Policy</a>, and I am responsible for
              complying with applicable telemarketing, email, and privacy laws
              (TCPA, CAN-SPAM, and state rules) in my outreach.
            </span>
          </label>
        )}
        <button type="submit" style={{
          width: '100%', padding: '13px', backgroundColor: theme.primary, color: '#fff',
          border: 'none', borderRadius: '6px', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem', marginTop: '6px',
        }}>{isSignUp ? 'CREATE ACCOUNT' : 'SIGN IN'}</button>
        <button type="button" onClick={() => { setIsSignUp(!isSignUp); setMsg({ text: '', color: '' }); }}
          style={{ width: '100%', marginTop: '16px', background: 'none', border: 'none', color: theme.textMute, cursor: 'pointer', fontSize: '0.85rem' }}>
          {isSignUp ? '← Back to sign in' : "Don't have an account? Register"}
        </button>
        {msg.text && <p style={{ color: msg.color, marginTop: '16px', fontSize: '0.85rem', textAlign: 'center' }}>{msg.text}</p>}
      </form>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Terms re-acceptance gate — blocking modal shown when a signed-in user's
// recorded terms version is missing or stale. They can't use the app until
// they re-agree; acceptance is recorded (version + timestamp) server-side.
// ─────────────────────────────────────────────────────────────────────────
const TermsGate = ({ onAccept, accepting }) => {
  const [checked, setChecked] = useState(false);
  const ready = checked && !accepting;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.85)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
      fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: '14px',
        maxWidth: '520px', width: '100%', padding: '28px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <h2 style={{ color: theme.heading, margin: '0 0 10px', fontSize: '1.3rem' }}>Please review our Terms before continuing</h2>
        <p style={{ color: theme.text, fontSize: '0.92rem', lineHeight: 1.6, margin: '0 0 14px' }}>
          Contacting the businesses in this product carries real compliance responsibilities. Before you
          continue, please read and agree to our{' '}
          <a href="/terms" target="_blank" rel="noreferrer" style={{ color: theme.primary }}>Terms of Service &amp; Acceptable Use Policy</a>.
          You are responsible for complying with applicable telemarketing, email, and privacy laws
          (TCPA, CAN-SPAM, and state rules) in how you reach out.
        </p>
        <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', color: theme.text,
          fontSize: '0.9rem', lineHeight: 1.5, cursor: 'pointer', margin: '0 0 18px' }}>
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)}
            style={{ marginTop: '3px', cursor: 'pointer', flexShrink: 0 }} />
          <span>I have read and agree to the Terms of Service &amp; Acceptable Use Policy.</span>
        </label>
        <button onClick={onAccept} disabled={!ready}
          style={{ width: '100%', padding: '13px', background: ready ? theme.primary : theme.border,
            color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700,
            cursor: ready ? 'pointer' : 'not-allowed', fontSize: '0.95rem' }}>
          {accepting ? 'Saving…' : 'Agree & Continue'}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Dashboard with lead lifecycle (Milestone 2)
// ─────────────────────────────────────────────────────────────────────────
const Dashboard = ({ token, onLogout }) => {
  const [leads, setLeads] = useState([]);
  // Total rows matching the current filters across the whole database, from
  // the X-Total-Count header. Null until the first response lands.
  const [total, setTotal] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [searchInput, setSearchInput] = useState('');  // what the user is typing
  const [search, setSearch] = useState('');            // debounced value sent to the API
  const [filter, setFilter] = useState('all');
  // Multi-select: several categories/counties can be active at once, sent as
  // repeated query params which the API ORs — "Duval or Clay" is expressible.
  const [professions, setProfessions] = useState([]);
  const [sortBy, setSortBy] = useState('');
  // Geographic filters — applied server-side so they search the whole
  // database, not just the rows loaded on this page.
  const [counties, setCounties] = useState([]);
  const [cityInput, setCityInput] = useState('');  // what the user is typing
  const [city, setCity] = useState('');            // debounced value sent to the API
  const [hasContact, setHasContact] = useState(false);
  // Pagination — offset-based; jumps back to the first page on any filter change.
  const [pageSize, setPageSize] = useState(100);
  const [offset, setOffset] = useState(0);
  // Pool counters from the X-Count-* headers: matching rows across the whole
  // database, split into available vs already mine. Null until the API sends them.
  const [counts, setCounts] = useState({ total: null, available: null, mine: null, due: null });
  // Facet counts for the dropdowns, from /leads/facets. Empty on older API
  // revisions, in which case the dropdowns render without counts.
  const [facets, setFacets] = useState({ county: [], profession: [] });
  // Bulk selection (desktop table). allMatching = "select all N matching":
  // the add is then done by filter server-side, not by a list of ids.
  const [selected, setSelected] = useState(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [quota, setQuota] = useState({ used: 0, max: 100 });
  // Terms re-acceptance gate: true when the user's recorded terms version is
  // missing or stale vs. what the app currently requires. Blocks the app.
  const [needsTerms, setNeedsTerms] = useState(false);
  const [acceptingTerms, setAcceptingTerms] = useState(false);
  const [expandedLead, setExpandedLead] = useState(null);
  // Record detail per entity id ({loading} | {data} | {error}), fetched
  // lazily on first expand and cached for the session. On an older API the
  // endpoint 404s and the drawer falls back to the pipeline controls alone.
  const [details, setDetails] = useState({});
  // Activity timeline per assignment id ({loading} | {data}), fetched when a
  // taken lead's drawer opens and invalidated whenever we write an event.
  const [timelines, setTimelines] = useState({});
  // Saved searches — named filter sets the daily job emails digests for.
  // savedSupported flips off when the API doesn't have the feature yet, and
  // the whole UI section hides.
  const [savedSearches, setSavedSearches] = useState([]);
  const [savedSupported, setSavedSupported] = useState(true);
  const [savedOpen, setSavedOpen] = useState(false);   // "Saved" dropdown
  const [saveOpen, setSaveOpen] = useState(false);     // save-current popover
  const [saveName, setSaveName] = useState('');
  const [saveCadence, setSaveCadence] = useState('daily');
  const [saveMsg, setSaveMsg] = useState('');
  // Lists — named sets of leads; what an export or a campaign is scoped to.
  // listsSupported flips off (and the UI hides) on an older API.
  const [lists, setLists] = useState([]);
  const [listsSupported, setListsSupported] = useState(true);
  const [listFilter, setListFilter] = useState('');   // list id, or '' = all
  const [listOpen, setListOpen] = useState(false);    // bulk-bar add-to-list dropdown
  const [newListName, setNewListName] = useState('');
  const [listMsg, setListMsg] = useState('');
  const isMobile = useIsMobile();
  const [filtersOpen, setFiltersOpen] = useState(false);

  // The filter state as query params, shared by /leads and /leads/facets.
  // county and profession repeat (?county=Duval&county=Clay) — the API ORs them.
  const filterParams = useCallback(() => {
    const p = new URLSearchParams();
    if (search) p.append('search', search);
    if (filter && filter !== 'all') p.append('assignment', filter);
    if (hasContact) p.append('has_contact', 'true');
    professions.forEach((x) => p.append('profession', x));
    counties.forEach((x) => p.append('county', x));
    if (city) p.append('city', city);
    if (listFilter) p.append('list_id', listFilter);
    return p;
  }, [search, filter, hasContact, professions, counties, city, listFilter]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setStatusMsg('Syncing…');
    try {
      const p = filterParams();
      if (sortBy) p.append('sort_by', sortBy);
      p.append('limit', String(pageSize));
      p.append('offset', String(offset));
      const res = await api(`/api/v1/leads?${p}`, { token });
      const data = await res.json();
      setLeads(Array.isArray(data) ? data : []);
      const header = (name) => {
        const v = res.headers.get(name);
        return v === null ? null : Number(v);
      };
      setTotal(header('X-Total-Count'));
      setCounts({
        total: header('X-Count-Total'),
        available: header('X-Count-Available'),
        mine: header('X-Count-Mine'),
        due: header('X-Count-Due'),
      });
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
      setStatusMsg('Failed to load leads.');
    }
    setStatusMsg('');
    setLoading(false);
  }, [token, onLogout, filterParams, sortBy, pageSize, offset]);

  // Facet counts for the dropdowns. Older API revisions 404 here — the
  // dropdowns then just render without counts.
  const fetchFacets = useCallback(async () => {
    try {
      const res = await api(`/api/v1/leads/facets?${filterParams()}`, { token });
      if (res.ok) {
        const data = await res.json();
        setFacets({ county: data.county || [], profession: data.profession || [] });
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
    }
  }, [token, onLogout, filterParams]);

  // Debounce the text inputs so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setCity(cityInput.trim()), 400);
    return () => clearTimeout(t);
  }, [cityInput]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchQuota = useCallback(async () => {
    try {
      const res = await api('/api/v1/auth/me', { token });
      if (res.ok) {
        const u = (await res.json());
        setQuota({ used: u.leads_used_this_month ?? 0, max: u.max_leads_per_month ?? 100 });
        // Gate the app if they haven't accepted the current terms version.
        setNeedsTerms(u.terms_current === false);
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
    }
  }, [token, onLogout]);

  const acceptTerms = useCallback(async () => {
    setAcceptingTerms(true);
    try {
      const res = await api('/api/v1/auth/accept-terms', { token, method: 'POST' });
      if (res.ok) setNeedsTerms(false);
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
    } finally {
      setAcceptingTerms(false);
    }
  }, [token, onLogout]);

  const startCheckout = async (tier) => {
    try {
      const res = await api('/api/v1/billing/checkout', {
        token, method: 'POST', body: JSON.stringify({ tier }),
      });
      if (res.ok) {
        const { url } = await res.json();
        window.location.href = url;  // redirect to Stripe Checkout
      } else if (res.status === 503) {
        alert('Billing is not configured yet. Contact support to upgrade.');
      } else {
        const e = await res.json();
        alert(e.detail || 'Checkout failed.');
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
    }
  };

  const openPortal = async () => {
    try {
      const res = await api('/api/v1/billing/portal', { token, method: 'POST' });
      if (res.ok) {
        const { url } = await res.json();
        window.location.href = url;
      } else {
        const e = await res.json();
        alert(e.detail || 'Could not open billing portal.');
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
    }
  };

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { fetchFacets(); }, [fetchFacets]);
  useEffect(() => { fetchQuota(); }, [fetchQuota]);

  // Back to the first page whenever the query changes (not on offset itself).
  useEffect(() => { setOffset(0); }, [search, filter, hasContact, professions, counties, city, listFilter, sortBy, pageSize]);
  // A new result set invalidates the selection.
  useEffect(() => { setSelected(new Set()); setAllMatching(false); }, [leads]);

  // Assign a lead into the pipeline
  const assignLead = async (entityId) => {
    try {
      const res = await api(`/api/v1/leads/${entityId}/assign`, { token, method: 'POST' });
      if (res.ok) {
        await fetchQuota();
        await fetchLeads();
      } else if (res.status === 402) {
        alert('Monthly lead quota reached. Upgrade your plan to take more leads.');
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
    }
  };

  // Saved searches: load once; a 404 means the API predates the feature.
  const fetchSavedSearches = useCallback(async () => {
    try {
      const res = await api('/api/v1/saved-searches', { token });
      if (res.ok) {
        setSavedSearches(await res.json());
      } else if (res.status === 404) {
        setSavedSupported(false);
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
    }
  }, [token, onLogout]);
  useEffect(() => { fetchSavedSearches(); }, [fetchSavedSearches]);

  // Lists: load once; a 404 means the API predates the feature.
  const fetchLists = useCallback(async () => {
    try {
      const res = await api('/api/v1/lists', { token });
      if (res.ok) {
        setLists(await res.json());
      } else if (res.status === 404) {
        setListsSupported(false);
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
    }
  }, [token, onLogout]);
  useEffect(() => { fetchLists(); }, [fetchLists]);

  // Add the selected rows to a list — creating it first when a new name is
  // given. Reports the add/skip split the API returns.
  const addSelectionToList = async (listId, createName) => {
    setListMsg('');
    try {
      let id = listId;
      let name = (lists.find((x) => x.id === listId) || {}).name;
      if (createName) {
        const created = await api('/api/v1/lists', {
          token, method: 'POST', body: JSON.stringify({ name: createName.trim() }),
        });
        if (!created.ok) {
          const e = await created.json().catch(() => ({}));
          setListMsg(e.detail || 'Could not create the list.');
          return;
        }
        const row = await created.json();
        id = row.id;
        name = row.name;
      }
      const res = await api(`/api/v1/lists/${id}/entries`, {
        token, method: 'POST', body: JSON.stringify({ entity_ids: [...selected] }),
      });
      if (res.ok) {
        const r = await res.json();
        const parts = [`Added ${r.added} to "${name}"`];
        if (r.skipped_existing) parts.push(`${r.skipped_existing} already on it`);
        setListMsg(parts.join(' · '));
        setListOpen(false);
        setNewListName('');
        await fetchLists();
      } else {
        const e = await res.json().catch(() => ({}));
        setListMsg(e.detail || 'Could not add to the list.');
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
      setListMsg('Could not add to the list.');
    }
  };

  // One reset for every filter — used by the Clear all pill and the empty
  // state, so a dead end always has a way out.
  const clearAllFilters = () => {
    setSearchInput(''); setCityInput(''); setCounties([]); setProfessions([]);
    setHasContact(false); setFilter('all'); setListFilter('');
  };

  // The current filter state in the shape the API stores (and GET /leads takes).
  const currentFilters = () => ({
    search: search || null,
    county: counties.length ? counties : null,
    city: city || null,
    profession: professions.length ? professions : null,
    has_contact: hasContact,
  });

  const saveCurrentSearch = async () => {
    const name = saveName.trim();
    if (!name) { setSaveMsg('Give it a name first.'); return; }
    try {
      const res = await api('/api/v1/saved-searches', {
        token, method: 'POST',
        body: JSON.stringify({ name, filters: currentFilters(), cadence: saveCadence }),
      });
      if (res.ok) {
        setSaveOpen(false);
        setSaveName('');
        setSaveMsg('');
        await fetchSavedSearches();
      } else {
        const e = await res.json().catch(() => ({}));
        setSaveMsg(e.detail || 'Could not save.');
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
      setSaveMsg('Could not save.');
    }
  };

  const applySavedSearch = (s) => {
    const f = s.filters || {};
    setSearchInput(f.search || '');
    setCityInput(f.city || '');
    setCounties(f.county || []);
    setProfessions(f.profession || []);
    setHasContact(Boolean(f.has_contact));
    setSavedOpen(false);
  };

  const deleteSavedSearch = async (id) => {
    try {
      const res = await api(`/api/v1/saved-searches/${id}`, { token, method: 'DELETE' });
      if (res.ok || res.status === 204) {
        setSavedSearches((prev) => prev.filter((s) => s.id !== id));
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
    }
  };

  // Activity timeline for one assignment, cached until we write an event.
  const fetchTimeline = async (assignmentId) => {
    setTimelines((t) => ({ ...t, [assignmentId]: { loading: true } }));
    try {
      const res = await api(`/api/v1/assignments/${assignmentId}/events`, { token });
      if (res.ok) {
        const data = await res.json();
        setTimelines((t) => ({ ...t, [assignmentId]: { data } }));
      } else {
        setTimelines((t) => ({ ...t, [assignmentId]: { data: [] } }));
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
      setTimelines((t) => ({ ...t, [assignmentId]: { data: [] } }));
    }
  };
  // After this session writes an event, refetch right away so the open
  // drawer's Activity section shows it instead of waiting for a reopen.
  const invalidateTimeline = (assignmentId) => {
    if (!assignmentId) return;
    fetchTimeline(assignmentId);
  };

  // Toggle a record open; fetch its provenance detail on first open and the
  // activity timeline when it's a taken lead.
  const openLead = async (id) => {
    const closing = expandedLead === id;
    setExpandedLead(closing ? null : id);
    if (closing) return;
    const lead = leads.find((l) => l.id === id);
    if (lead && lead.assignment_id && !timelines[lead.assignment_id]) {
      fetchTimeline(lead.assignment_id);
    }
    if (details[id]) return;
    setDetails((d) => ({ ...d, [id]: { loading: true } }));
    try {
      const res = await api(`/api/v1/leads/${id}`, { token });
      if (res.ok) {
        const data = await res.json();
        setDetails((d) => ({ ...d, [id]: { data } }));
      } else {
        setDetails((d) => ({ ...d, [id]: { error: true } }));
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
      setDetails((d) => ({ ...d, [id]: { error: true } }));
    }
  };

  // Schedule (or clear) the follow-up date. Noon UTC keeps the calendar day
  // stable in any US timezone; the server treats it date-granularly anyway.
  const setFollowUp = async (lead, dateStr) => {
    if (!lead.assignment_id) return;
    const value = dateStr ? `${dateStr}T12:00:00+00:00` : null;
    try {
      const res = await api(`/api/v1/assignments/${lead.assignment_id}`, {
        token, method: 'PATCH', body: JSON.stringify({ next_action_at: value }),
      });
      if (res.ok) {
        setLeads((prev) => prev.map((l) =>
          l.id === lead.id ? { ...l, assignment_next_action: value } : l));
        invalidateTimeline(lead.assignment_id);
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
    }
  };

  // Bulk add — one request for the whole selection instead of one per row.
  // The server checks quota once, takes what fits, and reports what it
  // skipped, so a partial take succeeds rather than failing with a 402.
  const bulkAssign = async () => {
    setBulkBusy(true);
    setBulkMsg('');
    try {
      const body = allMatching
        ? {
            filter: {
              search: search || null,
              county: counties.length ? counties : null,
              city: city || null,
              profession: professions.length ? professions : null,
              has_contact: hasContact,
            },
            limit: 500,  // server cap per request
          }
        : { entity_ids: [...selected] };
      const res = await api('/api/v1/leads/assign', {
        token, method: 'POST', body: JSON.stringify(body),
      });
      if (res.ok) {
        const r = await res.json();
        const parts = [`Added ${r.assigned} lead${r.assigned !== 1 ? 's' : ''} to your pipeline`];
        if (r.skipped_already_mine) parts.push(`${r.skipped_already_mine} already yours`);
        if (r.skipped_quota) parts.push(`${r.skipped_quota} skipped — monthly quota reached`);
        setBulkMsg(parts.join(' · '));
        setSelected(new Set());
        setAllMatching(false);
        await fetchQuota();
        await fetchLeads();
      } else {
        const e = await res.json().catch(() => ({}));
        setBulkMsg(e.detail || 'Bulk add failed.');
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
      setBulkMsg('Bulk add failed.');
    }
    setBulkBusy(false);
  };

  // Update assignment status/notes
  const updateStatus = async (lead, newStatus) => {
    if (!lead.assignment_id) return;
    try {
      const res = await api(`/api/v1/assignments/${lead.assignment_id}`, {
        token, method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        setLeads((prev) => prev.map((l) => l.id === lead.id ? { ...l, assignment_status: newStatus } : l));
        invalidateTimeline(lead.assignment_id);
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
    }
  };

  const saveNotes = async (lead, notes) => {
    if (!lead.assignment_id) return;
    try {
      const res = await api(`/api/v1/assignments/${lead.assignment_id}`, {
        token, method: 'PATCH', body: JSON.stringify({ notes }),
      });
      if (res.ok) {
        setLeads((prev) => prev.map((l) => l.id === lead.id ? { ...l, assignment_notes: notes } : l));
        invalidateTimeline(lead.assignment_id);
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
    }
  };

  // Search, assignment state and has-contact are all applied server-side, so
  // the rows the API returns are exactly the rows to render. Filtering again
  // here is what made a search only ever match within the loaded page.
  const filteredLeads = leads;

  // Only unassigned rows are bulk-selectable — assigned ones are already in
  // the pipeline and the server would just skip them.
  const selectableIds = leads.filter((l) => !l.assignment_id).map((l) => l.id);

  // Facet counts, aggregated case-insensitively: county casing varies by
  // source feed ("DUVAL" vs "Duval", and DBPR's "Dade" inside "Miami-Dade"),
  // mirroring the substring semantics the county filter itself uses.
  const countyCount = (name) => {
    if (!facets.county.length) return null;
    const n = name.toLowerCase();
    return facets.county.reduce((sum, f) => {
      const v = (f.value || '').toLowerCase();
      return sum + (v.includes(n) || n.includes(v) ? f.count : 0);
    }, 0);
  };
  const professionCount = (key) => {
    if (!facets.profession.length) return null;
    const f = facets.profession.find((x) => x.value === key);
    return f ? f.count : 0;
  };
  const withCount = (label, c) => (c === null ? label : `${label} (${c.toLocaleString()})`);

  const exportToCSV = () => {
    // Contact basis mirrors the server export: inbound leads consented to
    // contact; everything else is public-record only.
    const basis = (l) => (l.lead_signal === 'inbound' ? 'Consented (opted-in)' : 'Public record');
    const rows = filteredLeads.map((l) => [
      l.business_name || '', l.phone || '', l.line_type || 'unknown', l.email || '',
      l.city || '', l.county || '', l.registered_agent || '', l.assignment_status || '',
      l.website || '', basis(l),
    ]);
    const csv = [['Business Name', 'Phone', 'Line Type', 'Email', 'City', 'County', 'Contact', 'Status', 'Website', 'Contact Basis'], ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `amanar_leads_${new Date().toISOString().split('T')[0]}.csv`; a.click();
    URL.revokeObjectURL(url);
    markExported();
  };

  // Server-side export (dialer formats need precise formatting).
  const exportFormat = async (fmt) => {
    try {
      // With a list filter active, the server export scopes to that list —
      // one campaign file, named after the list.
      const res = await api(
        `/api/v1/export?format=${fmt}${listFilter ? `&list_id=${listFilter}` : ''}`,
        { token },
      );
      if (!res.ok) {
        const e = await res.json();
        alert(e.detail || 'Export failed.');
        return;
      }
      const blob = await res.blob();
      const dispo = res.headers.get('Content-Disposition') || '';
      const fname = dispo.match(/filename="([^"]+)"/)?.[1] || `export.${fmt === 'phone_list' ? 'txt' : 'csv'}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      markExported();
    } catch (e) {
      if (e instanceof UnauthorizedError) return onLogout();
    }
  };

  const [exportOpen, setExportOpen] = useState(false);
  const [showAccount, setShowAccount] = useState(false);

  // Onboarding checklist — pinned until every step is done or it's
  // dismissed. Progress comes from live state; the export step is a
  // localStorage breadcrumb since the server doesn't track exports.
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => {
    try { return localStorage.getItem('onboarding_dismissed') === '1'; } catch { return false; }
  });
  const [hasExported, setHasExported] = useState(() => {
    try { return localStorage.getItem('has_exported') === '1'; } catch { return false; }
  });
  const markExported = () => {
    try { localStorage.setItem('has_exported', '1'); } catch { /* per-viewer nicety only */ }
    setHasExported(true);
  };
  const dismissOnboarding = () => {
    try { localStorage.setItem('onboarding_dismissed', '1'); } catch { /* ditto */ }
    setOnboardingDismissed(true);
  };
  const onboardingSteps = [
    { label: 'Take your first lead', hint: 'click + Add on any row', done: (counts.mine ?? 0) > 0 },
    { label: 'Save a search', hint: 'new matches land in your inbox every morning', done: savedSearches.length > 0 },
    { label: 'Run your first export', hint: 'Export → CSV or a dialer format', done: hasExported },
  ];
  const onboardingDone = onboardingSteps.filter((s) => s.done).length;
  const showOnboarding = !onboardingDismissed
    && counts.mine !== null                       // wait for real data — no flash
    && onboardingDone < onboardingSteps.length;

  return (
    <div style={{ backgroundColor: theme.bg, minHeight: '100vh', padding: isMobile ? '14px' : '32px',
      fontFamily: 'system-ui, -apple-system, sans-serif', color: theme.text }}>
      {needsTerms && <TermsGate onAccept={acceptTerms} accepting={acceptingTerms} />}
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
          <h1 style={{ color: theme.heading, margin: 0, fontSize: isMobile ? '1.25rem' : '1.6rem', letterSpacing: '1px' }}>AMANAR</h1>
          <span style={{ color: theme.primary, fontSize: '0.8rem', fontWeight: 600 }}>LEAD ENGINE</span>
          {statusMsg && !isMobile && <span style={{ color: theme.textMute, fontSize: '0.85rem' }}>{statusMsg}</span>}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Quota, always on screen — a user should feel the wall coming,
              not discover it at 402. Click opens plans. */}
          <button onClick={() => setShowAccount(true)} title="Monthly lead quota — click for plans"
            style={{
              display: 'inline-flex', flexDirection: 'column', gap: '4px', cursor: 'pointer',
              padding: '7px 12px', borderRadius: '6px', background: theme.surface,
              border: `1px solid ${quota.used >= quota.max ? theme.danger : theme.border}`,
            }}>
            <span style={{
              fontSize: '0.72rem', fontWeight: 600, lineHeight: 1,
              color: quota.used >= quota.max ? theme.danger
                : quota.used / quota.max >= 0.8 ? '#FBBF24' : theme.textMute,
            }}>
              {quota.used} / {quota.max} leads
            </span>
            <span style={{ display: 'block', width: '76px', height: '3px', borderRadius: '2px', backgroundColor: theme.surfaceAlt }}>
              <span style={{
                display: 'block', height: '100%', borderRadius: '2px',
                width: `${Math.min(100, (quota.used / Math.max(1, quota.max)) * 100)}%`,
                backgroundColor: quota.used >= quota.max ? theme.danger
                  : quota.used / quota.max >= 0.8 ? '#FBBF24' : theme.primary,
              }} />
            </span>
          </button>
          <div style={{ position: 'relative' }}>
            <button onClick={() => setExportOpen(!exportOpen)} style={btn(theme.surface, theme.primary)}>
              Export ▾
            </button>
            {exportOpen && (
              <div style={{
                position: 'absolute', right: 0, top: '110%', backgroundColor: theme.surface,
                border: `1px solid ${theme.border}`, borderRadius: '8px', minWidth: '220px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)', zIndex: 100, overflow: 'hidden',
              }}>
                <ExportItem label="CSV (generic)" hint="All fields, spreadsheet-ready" onClick={() => { exportToCSV(); setExportOpen(false); }} />
                <ExportItem label="VanDyke / A Dialer" hint="Phone-first, FL dialer format" onClick={() => { exportFormat('vandyke'); setExportOpen(false); }} />
                <ExportItem label="Phone list" hint="One number per line (E.164)" onClick={() => { exportFormat('phone_list'); setExportOpen(false); }} />
                <div style={{ borderTop: `1px solid ${theme.border}`, padding: '8px 14px', color: theme.textMute, fontSize: '0.75rem' }}>
                  Direct dialer API — coming soon
                </div>
              </div>
            )}
          </div>
          <button onClick={fetchLeads} style={btn(theme.surface, theme.text)}>Refresh</button>
          <a href="/guide" target="_blank" rel="noreferrer"
            style={{ ...btn(theme.surface, theme.text), textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>Guide</a>
          <button onClick={() => setShowAccount(true)} style={btn(theme.surface, theme.text)}>Account</button>
          <button onClick={onLogout} style={btn(theme.danger, '#fff')}>Sign out</button>
        </div>
      </div>

      {/* Getting-started checklist — the first three actions that make the
          product click, with live progress. Dismissible; gone when done. */}
      {showOnboarding && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
          backgroundColor: theme.surface, border: `1px solid ${theme.border}`,
          borderRadius: '10px', padding: '12px 16px', marginBottom: '16px',
        }}>
          <span style={{ color: theme.heading, fontSize: '0.85rem', fontWeight: 600 }}>
            Getting started · {onboardingDone} of {onboardingSteps.length} done
          </span>
          {onboardingSteps.map((s) => (
            <span key={s.label} title={s.hint} style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              color: s.done ? theme.accent : theme.textMute, fontSize: '0.82rem',
            }}>
              {s.done ? '✓' : '○'} {s.label}
              {!s.done && (
                <span style={{ color: theme.textMute, fontSize: '0.75rem' }}>— {s.hint}</span>
              )}
            </span>
          ))}
          <button onClick={dismissOnboarding} aria-label="Dismiss getting started"
            style={{ marginLeft: 'auto', background: 'none', border: 'none',
              color: theme.textMute, cursor: 'pointer', fontSize: '0.9rem', padding: 0 }}>✕</button>
        </div>
      )}

      {/* Filter chips — horizontal-scroll strip on mobile so they stay one row */}
      <div style={{
        display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center',
        flexWrap: isMobile ? 'nowrap' : 'wrap',
        overflowX: isMobile ? 'auto' : 'visible',
        paddingBottom: isMobile ? '4px' : 0,
      }}>
        {FILTERS.map((f) => {
          const c = FILTER_COUNTS[f.key] ? counts[FILTER_COUNTS[f.key]] : null;
          // The Due today chip only exists once the API sends its counter —
          // on an older revision the filter value would 400.
          if (f.key === 'due' && c === null) return null;
          return (
            <button key={f.key} onClick={() => setFilter(f.key)} style={{
              padding: isMobile ? '8px 14px' : '6px 14px', borderRadius: '16px', fontSize: '0.8rem',
              cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              border: `1px solid ${filter === f.key ? theme.primary : theme.border}`,
              backgroundColor: filter === f.key ? theme.primary : theme.surface,
              color: filter === f.key ? '#fff' : theme.textMute, fontWeight: 500,
            }}>
              {f.label}
              {c !== null && !Number.isNaN(c) && (
                <span style={{ marginLeft: '6px', fontWeight: 700, opacity: filter === f.key ? 1 : 0.75 }}>
                  {c.toLocaleString()}
                </span>
              )}
            </button>
          );
        })}
        {isMobile && (
          <button onClick={() => setFiltersOpen(!filtersOpen)} style={{
            padding: '8px 14px', borderRadius: '16px', fontSize: '0.8rem', flexShrink: 0,
            border: `1px solid ${(professions.length || city || counties.length || sortBy) ? theme.primary : theme.border}`,
            backgroundColor: theme.surface, color: theme.heading, fontWeight: 600, whiteSpace: 'nowrap',
          }}>⚙ Filters{(professions.length || city || counties.length) ? ' •' : ''}</button>
        )}
        <div style={{
          marginLeft: isMobile ? 0 : 'auto',
          display: (isMobile && !filtersOpen) ? 'none' : 'flex',
          alignItems: 'center', gap: '12px',
          ...(isMobile ? { flexBasis: '100%', flexWrap: 'wrap' } : {}),
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: theme.textMute, fontSize: '0.8rem' }}>Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.surface,
                color: theme.heading,
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              <option value="">Newest first</option>
              <option value="score">Best match</option>
              <option value="enriched">Enriched first</option>
              <option value="name">Name A-Z</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: theme.textMute, fontSize: '0.8rem' }}>Category:</span>
            {/* Adder: picking a value appends a removable pill below, so several
                categories can be active at once. The select itself stays blank. */}
            <select
              value=""
              onChange={(e) => {
                const v = e.target.value;
                if (v && !professions.includes(v)) setProfessions([...professions, v]);
              }}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.surface,
                color: professions.length ? theme.heading : theme.textMute,
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              <option value="">{professions.length ? 'Add category…' : 'All categories'}</option>
              {PROFESSIONS.filter((p) => p.key && !professions.includes(p.key)).map((p) => (
                <option key={p.key} value={p.key}>{withCount(p.label, professionCount(p.key))}</option>
              ))}
            </select>
          </div>
          {listsSupported && lists.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: theme.textMute, fontSize: '0.8rem' }}>List:</span>
              <select
                value={listFilter}
                onChange={(e) => setListFilter(e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${listFilter ? theme.primary : theme.border}`,
                  backgroundColor: theme.surface,
                  color: listFilter ? theme.heading : theme.textMute,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                <option value="">All leads</option>
                {lists.map((x) => (
                  <option key={x.id} value={x.id}>{x.name} ({x.count})</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Search + geographic filters */}
      <div style={{
        display: (isMobile && !filtersOpen) ? 'none' : 'flex',
        flexWrap: 'wrap', alignItems: 'center', gap: '10px', marginBottom: '16px',
      }}>
        <input placeholder="Search business name…" value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)} style={{
            flex: isMobile ? '1 1 100%' : '1 1 220px', maxWidth: isMobile ? 'none' : '340px',
            padding: '11px 14px', borderRadius: '6px', border: `1px solid ${theme.border}`,
            backgroundColor: theme.surface, color: theme.heading, fontSize: '1rem',
          }} />
        <input placeholder="City (e.g. Tampa)…" value={cityInput}
          onChange={(e) => setCityInput(e.target.value)} style={{
            flex: isMobile ? '1 1 45%' : '0 1 180px', padding: '11px 14px',
            borderRadius: '6px', border: `1px solid ${theme.border}`,
            backgroundColor: theme.surface, color: theme.heading, fontSize: '1rem',
          }} />
        <select value="" onChange={(e) => {
          const v = e.target.value;
          if (v && !counties.includes(v)) setCounties([...counties, v]);
        }} style={{
          flex: isMobile ? '1 1 45%' : '0 1 auto',
          padding: '11px 12px', borderRadius: '6px', border: `1px solid ${theme.border}`,
          backgroundColor: theme.surface, color: counties.length ? theme.heading : theme.textMute,
          fontSize: '1rem', cursor: 'pointer',
        }}>
          <option value="">{counties.length ? 'Add county…' : 'All counties'}</option>
          {FL_COUNTIES.filter((c) => !counties.includes(c)).map((c) => (
            <option key={c} value={c}>{withCount(c, countyCount(c))}</option>
          ))}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: theme.textMute,
          fontSize: '0.85rem', cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={hasContact} onChange={(e) => setHasContact(e.target.checked)}
            style={{ cursor: 'pointer' }} />
          Has contact info
        </label>

        {/* Saved searches: save the current filter state under a name, get a
            morning email when new filings match it. Hidden on older APIs. */}
        {savedSupported && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center',
            marginLeft: isMobile ? 0 : 'auto', position: 'relative' }}>
            <button onClick={() => { setSaveOpen(!saveOpen); setSavedOpen(false); setSaveMsg(''); }}
              style={{ ...btn(theme.surface, theme.primary), padding: '8px 14px', fontSize: '0.8rem' }}>
              ★ Save search
            </button>
            {savedSearches.length > 0 && (
              <button onClick={() => { setSavedOpen(!savedOpen); setSaveOpen(false); }}
                style={{ ...btn(theme.surface, theme.text), padding: '8px 14px', fontSize: '0.8rem' }}>
                Saved ({savedSearches.length}) ▾
              </button>
            )}
            {saveOpen && (
              <div style={{
                position: 'absolute', right: 0, top: '115%', zIndex: 120, width: '280px',
                backgroundColor: theme.surface, border: `1px solid ${theme.border}`,
                borderRadius: '8px', padding: '14px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                display: 'grid', gap: '10px',
              }}>
                <input autoFocus placeholder="Name this search…" value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && saveCurrentSearch()}
                  style={{ padding: '9px 12px', borderRadius: '6px', border: `1px solid ${theme.border}`,
                    backgroundColor: theme.bg, color: theme.heading, fontSize: '0.85rem' }} />
                <select value={saveCadence} onChange={(e) => setSaveCadence(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: `1px solid ${theme.border}`,
                    backgroundColor: theme.bg, color: theme.heading, fontSize: '0.82rem', cursor: 'pointer' }}>
                  <option value="daily">Email me new matches every morning</option>
                  <option value="weekly">Email me new matches weekly</option>
                  <option value="none">No emails — just save the filters</option>
                </select>
                <button onClick={saveCurrentSearch}
                  style={{ ...btn(theme.primary, '#fff'), padding: '9px', fontSize: '0.85rem' }}>
                  Save
                </button>
                {saveMsg && <div style={{ color: theme.danger, fontSize: '0.78rem' }}>{saveMsg}</div>}
              </div>
            )}
            {savedOpen && (
              <div style={{
                position: 'absolute', right: 0, top: '115%', zIndex: 120, minWidth: '260px',
                backgroundColor: theme.surface, border: `1px solid ${theme.border}`,
                borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)', overflow: 'hidden',
              }}>
                {savedSearches.map((s) => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '9px 12px', borderBottom: `1px solid ${theme.border}` }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.surfaceAlt)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}>
                    <button onClick={() => applySavedSearch(s)} style={{
                      background: 'none', border: 'none', color: theme.heading, cursor: 'pointer',
                      fontSize: '0.85rem', fontWeight: 500, padding: 0, textAlign: 'left', flex: 1,
                    }}>{s.name}</button>
                    {s.cadence !== 'none' && (
                      <span style={{ color: theme.textMute, fontSize: '0.68rem', textTransform: 'uppercase' }}>
                        {s.cadence}
                      </span>
                    )}
                    <button onClick={() => deleteSavedSearch(s.id)} aria-label={`Delete ${s.name}`}
                      style={{ background: 'none', border: 'none', color: theme.textMute,
                        cursor: 'pointer', padding: 0, fontSize: '0.8rem' }}>✕</button>
                  </div>
                ))}
                <div style={{ padding: '7px 12px', color: theme.textMute, fontSize: '0.72rem' }}>
                  Click a search to apply it · digests arrive after the morning data load
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Applied filters as removable pills — the applied state stays legible
          without opening the dropdowns — plus one-click Clear all. */}
      {(professions.length > 0 || counties.length > 0 || search || city || hasContact || listFilter) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          {professions.map((key) => (
            <FilterPill key={`p-${key}`}
              label={(PROFESSIONS.find((p) => p.key === key) || { label: key }).label}
              onRemove={() => setProfessions(professions.filter((x) => x !== key))} />
          ))}
          {counties.map((c) => (
            <FilterPill key={`c-${c}`} label={`${c} County`}
              onRemove={() => setCounties(counties.filter((x) => x !== c))} />
          ))}
          {city && <FilterPill label={`City: ${city}`} onRemove={() => setCityInput('')} />}
          {search && <FilterPill label={`"${search}"`} onRemove={() => setSearchInput('')} />}
          {hasContact && <FilterPill label="Has contact info" onRemove={() => setHasContact(false)} />}
          {listFilter && (
            <FilterPill
              label={`List: ${(lists.find((x) => x.id === listFilter) || { name: '…' }).name}`}
              onRemove={() => setListFilter('')} />
          )}
          <button onClick={clearAllFilters} style={{
            padding: '5px 10px', border: 'none', background: 'none', color: theme.textMute,
            fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline',
          }}>Clear all</button>
        </div>
      )}

      {/* Bulk selection bar — select many, act once */}
      {!isMobile && (selected.size > 0 || allMatching) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
          backgroundColor: theme.surfaceAlt, border: `1px solid ${theme.primary}55`,
          borderRadius: '8px', padding: '10px 14px', marginBottom: '12px',
        }}>
          <span style={{ color: theme.heading, fontSize: '0.85rem', fontWeight: 600 }}>
            {allMatching
              ? `All ${(total ?? 0).toLocaleString()} matching selected${(total ?? 0) > 500 ? ' · adds 500 per batch' : ''}`
              : `${selected.size} selected`}
          </span>
          {!allMatching && selectableIds.length > 0 && selected.size === selectableIds.length
            && total !== null && total > leads.length && (
            <button onClick={() => setAllMatching(true)} style={{
              background: 'none', border: 'none', color: theme.primary, cursor: 'pointer',
              fontSize: '0.85rem', textDecoration: 'underline', padding: 0,
            }}>Select all {total.toLocaleString()} matching</button>
          )}
          <button onClick={bulkAssign} disabled={bulkBusy} style={{
            ...btn(theme.primary, '#fff'), padding: '7px 16px', fontSize: '0.8rem',
            opacity: bulkBusy ? 0.6 : 1,
          }}>
            {bulkBusy ? 'Adding…' : `+ Add ${allMatching ? 'all matching' : selected.size} to my pipeline`}
          </button>
          {/* Lists take explicit ids, so the whole-filter selection mode
              doesn't offer it — select rows on the page instead. */}
          {listsSupported && !allMatching && selected.size > 0 && (
            <div style={{ position: 'relative' }}>
              <button onClick={() => setListOpen(!listOpen)}
                style={{ ...btn(theme.surface, theme.primary), padding: '7px 14px', fontSize: '0.8rem' }}>
                Add to list ▾
              </button>
              {listOpen && (
                <div style={{
                  position: 'absolute', left: 0, top: '115%', zIndex: 120, minWidth: '240px',
                  backgroundColor: theme.surface, border: `1px solid ${theme.border}`,
                  borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)', overflow: 'hidden',
                }}>
                  {lists.map((x) => (
                    <div key={x.id} onClick={() => addSelectionToList(x.id)} style={{
                      padding: '9px 12px', cursor: 'pointer', color: theme.heading,
                      fontSize: '0.85rem', borderBottom: `1px solid ${theme.border}`,
                    }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.surfaceAlt)}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}>
                      {x.name} <span style={{ color: theme.textMute, fontSize: '0.75rem' }}>({x.count})</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: '6px', padding: '9px 12px' }}>
                    <input placeholder="New list…" value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && newListName.trim() && addSelectionToList(null, newListName)}
                      style={{ flex: 1, padding: '7px 10px', borderRadius: '6px', border: `1px solid ${theme.border}`,
                        backgroundColor: theme.bg, color: theme.heading, fontSize: '0.8rem' }} />
                    <button onClick={() => newListName.trim() && addSelectionToList(null, newListName)}
                      style={{ ...btn(theme.primary, '#fff'), padding: '7px 12px', fontSize: '0.78rem' }}>
                      Create
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <button onClick={() => { setSelected(new Set()); setAllMatching(false); setListOpen(false); }} style={{
            background: 'none', border: 'none', color: theme.textMute, cursor: 'pointer',
            fontSize: '0.8rem', padding: 0,
          }}>Clear selection</button>
        </div>
      )}
      {listMsg && (
        <div style={{ color: theme.accent, fontSize: '0.85rem', marginBottom: '12px' }}>{listMsg}</div>
      )}
      {bulkMsg && (
        <div style={{ color: theme.accent, fontSize: '0.85rem', marginBottom: '12px' }}>{bulkMsg}</div>
      )}

      {/* Lead list — cards on mobile, table on desktop */}
      <div style={{ backgroundColor: theme.surface, borderRadius: '10px', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: theme.textMute }}>Loading leads…</div>
        ) : filteredLeads.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: theme.textMute }}>
            <div style={{ marginBottom: '14px' }}>No leads match these filters.</div>
            <button onClick={clearAllFilters}
              style={{ ...btn(theme.primary, '#fff'), padding: '9px 18px', fontSize: '0.85rem' }}>
              Clear all filters
            </button>
          </div>
        ) : isMobile ? (
          <div>
            {filteredLeads.map((l) => (
              <LeadCard
                key={l.id}
                lead={l}
                expanded={expandedLead === l.id}
                onToggle={() => openLead(l.id)}
                detail={details[l.id]}
                timeline={l.assignment_id ? timelines[l.assignment_id] : null}
                onFollowUp={setFollowUp}
                onAssign={assignLead}
                onStatus={updateStatus}
                onSaveNotes={saveNotes}
              />
            ))}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: '1000px', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ padding: '10px 12px', borderBottom: `2px solid ${theme.border}`, width: '34px' }}>
                  <input type="checkbox"
                    checked={selectableIds.length > 0 && selected.size === selectableIds.length}
                    disabled={selectableIds.length === 0}
                    onChange={(e) => {
                      setAllMatching(false);
                      setSelected(e.target.checked ? new Set(selectableIds) : new Set());
                    }}
                    title="Select all on this page"
                    style={{ cursor: 'pointer' }} />
                </th>
                {['Business', 'Score', 'Category', 'City', 'County', 'Phone', 'Website', 'Contact', 'Email', 'Filing Date', 'Status', ''].map((h, i) => (
                  <th key={i} style={{ padding: '10px 12px', textAlign: 'left', borderBottom: `2px solid ${theme.border}`,
                    color: theme.textMute, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((l) => (
                <React.Fragment key={l.id}>
                  <tr style={{ borderBottom: `1px solid ${theme.border}`, cursor: 'pointer' }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.surfaceAlt)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    onClick={() => openLead(l.id)}>
                    <td style={{ ...cell(), width: '34px' }} onClick={(e) => e.stopPropagation()}>
                      {!l.assignment_id && (
                        <input type="checkbox" checked={selected.has(l.id)}
                          onChange={() => {
                            setAllMatching(false);
                            setSelected((prev) => {
                              const next = new Set(prev);
                              if (next.has(l.id)) next.delete(l.id); else next.add(l.id);
                              return next;
                            });
                          }}
                          style={{ cursor: 'pointer' }} />
                      )}
                    </td>
                    <td style={cell(theme.heading, true)}>
                      {l.business_name || '—'}
                      {l.lead_signal && SIGNAL_BADGES[l.lead_signal] && (
                        <span style={{
                          marginLeft: '8px', padding: '2px 7px', borderRadius: '10px',
                          fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.5px',
                          textTransform: 'uppercase', whiteSpace: 'nowrap',
                          color: SIGNAL_BADGES[l.lead_signal].fg,
                          backgroundColor: SIGNAL_BADGES[l.lead_signal].bg,
                        }}>{SIGNAL_BADGES[l.lead_signal].label}</span>
                      )}
                      {isUpcoming(l.filing_date) && <UpcomingBadge />}
                      <ChainTag lead={l} />
                    </td>
                    <td style={cell()}><ScoreBadge score={l.lead_score} /></td>
                    <td style={cell(theme.textMute)}>{l.profession || '—'}</td>
                    <td style={cell()}>{l.city || '—'}</td>
                    <td style={cell(theme.textMute)}>{l.county || '—'}</td>
                    <td style={cell('#93C5FD')}>{l.phone || '—'}{l.phone && <LineTypeTag type={l.line_type} />}</td>
                    <td style={cell()}>
                      {l.website ? (
                        <a href={l.website.startsWith('http') ? l.website : `https://${l.website}`} target="_blank" rel="noreferrer"
                          style={{ color: theme.primary, textDecoration: 'none', fontSize: '0.8rem' }}>{l.website.length > 15 ? l.website.substring(0, 15) + '...' : l.website}</a>
                      ) : '—'}
                    </td>
                    <td style={cell(theme.textMute)}>
                      <div title={l.registered_agent || l.operator_type || ''} style={{
                        maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{l.registered_agent || l.operator_type || '—'}</div>
                    </td>
                    <td style={cell()}>
                      {l.email ? (
                        <a href={`mailto:${l.email}`} onClick={(e) => e.stopPropagation()} title={l.email}
                          style={{ color: theme.primary, textDecoration: 'none', fontSize: '0.8rem',
                            display: 'inline-block', maxWidth: '170px', overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                          {l.email}
                        </a>
                      ) : '—'}
                    </td>
                    <td style={cell(theme.textMute)}>
                      {l.filing_date
                        ? (isUpcoming(l.filing_date)
                            ? `effective ${new Date(l.filing_date).toLocaleDateString()}`
                            : new Date(l.filing_date).toLocaleDateString())
                        : '—'}
                    </td>
                    <td style={cell()}>
                      {l.assignment_id ? (
                        <StatusBadge status={l.assignment_status} />
                      ) : (
                        <span style={{ color: theme.textMute, fontSize: '0.8rem' }}>—</span>
                      )}
                    </td>
                    <td style={{ ...cell(), textAlign: 'right' }}>
                      {!l.assignment_id ? (
                        <button onClick={(e) => { e.stopPropagation(); assignLead(l.id); }}
                          style={{ ...btn(theme.primary, '#fff'), padding: '5px 12px', fontSize: '0.78rem' }}>+ Add</button>
                      ) : (
                        <span style={{ color: theme.textMute, fontSize: '0.75rem' }}>{expandedLead === l.id ? '▲' : '▼'}</span>
                      )}
                    </td>
                  </tr>
                  {expandedLead === l.id && (
                    <tr style={{ backgroundColor: theme.bg }}>
                      <td colSpan={13} style={{ padding: '16px 24px' }}>
                        <LeadDrawer lead={l} detail={details[l.id]}
                          timeline={l.assignment_id ? timelines[l.assignment_id] : null}
                          onAssign={assignLead} onStatus={updateStatus}
                          onSaveNotes={saveNotes} onFollowUp={setFollowUp} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      {/* Pagination footer — you never see a list without knowing how big it
          is: range, page controls and page size in one row. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginTop: '12px',
        color: theme.textMute, fontSize: '0.8rem',
      }}>
        <span>
          {total === null || Number.isNaN(total)
            ? `${filteredLeads.length} lead${filteredLeads.length !== 1 ? 's' : ''} shown`
            : total === 0
              ? 'No matching leads'
              : `${(offset + 1).toLocaleString()}–${(offset + filteredLeads.length).toLocaleString()} of ${total.toLocaleString()} matching lead${total !== 1 ? 's' : ''}`}
        </span>
        {total !== null && !Number.isNaN(total) && total > pageSize && (
          <span style={{ display: 'inline-flex', gap: '6px' }}>
            <button onClick={() => setOffset(Math.max(0, offset - pageSize))} disabled={offset === 0}
              style={{ ...btn(theme.surface, offset === 0 ? theme.textMute : theme.text),
                padding: '5px 12px', fontSize: '0.78rem',
                cursor: offset === 0 ? 'default' : 'pointer' }}>← Prev</button>
            <button onClick={() => setOffset(offset + pageSize)} disabled={offset + pageSize >= total}
              style={{ ...btn(theme.surface, offset + pageSize >= total ? theme.textMute : theme.text),
                padding: '5px 12px', fontSize: '0.78rem',
                cursor: offset + pageSize >= total ? 'default' : 'pointer' }}>Next →</button>
          </span>
        )}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          Per page:
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{
            padding: '4px 8px', borderRadius: '6px', border: `1px solid ${theme.border}`,
            backgroundColor: theme.surface, color: theme.heading, fontSize: '0.78rem', cursor: 'pointer',
          }}>
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <span style={{ marginLeft: isMobile ? 0 : 'auto' }}>
          Chain detection covers major brands — an unlabeled business isn't verified independent
        </span>
      </div>

      {showAccount && (
        <AccountScreen
          quota={quota}
          onClose={() => setShowAccount(false)}
          onCheckout={startCheckout}
          onPortal={openPortal}
        />
      )}
    </div>
  );
};

// Mobile lead card — replaces a table row on phones. Phone and email are
// tap-to-act (tel: / mailto:), which is the whole point of working leads on a
// phone in the field.
const LeadCard = ({ lead: l, expanded, onToggle, onAssign, onStatus, onSaveNotes, detail, timeline, onFollowUp }) => {
  const sig = l.lead_signal && SIGNAL_BADGES[l.lead_signal];
  const websiteHref = l.website
    ? (l.website.startsWith('http') ? l.website : `https://${l.website}`)
    : null;
  const contact = l.registered_agent || l.operator_type;
  return (
    <div style={{ borderBottom: `1px solid ${theme.border}`, padding: '14px 16px' }}>
      <div onClick={onToggle} style={{ cursor: 'pointer' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: theme.heading, fontWeight: 600, fontSize: '0.98rem' }}>
              {l.business_name || '—'}
            </div>
            <div style={{ color: theme.textMute, fontSize: '0.8rem', marginTop: '3px' }}>
              {[l.profession, l.city, l.county && `${l.county} County`].filter(Boolean).join(' · ') || '—'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexShrink: 0, alignItems: 'center' }}>
            <ScoreBadge score={l.lead_score} />
            {sig && (
              <span style={{
                flexShrink: 0, padding: '3px 8px', borderRadius: '10px', fontSize: '0.6rem',
                fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase',
                color: sig.fg, backgroundColor: sig.bg,
              }}>{sig.label}</span>
            )}
            {isUpcoming(l.filing_date) && <UpcomingBadge />}
            <ChainTag lead={l} />
          </div>
        </div>
      </div>

      {/* Tap-to-act contact row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
        {l.phone && (
          <a href={`tel:${l.phone.replace(/[^\d+]/g, '')}`} onClick={(e) => e.stopPropagation()}
            style={contactChip(theme.primary)}>📞 {l.phone}<LineTypeTag type={l.line_type} /></a>
        )}
        {l.email && (
          <a href={`mailto:${l.email}`} onClick={(e) => e.stopPropagation()}
            style={contactChip(theme.accent)}>✉️ Email</a>
        )}
        {websiteHref && (
          <a href={websiteHref} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
            style={contactChip(theme.textMute)}>🌐 Website</a>
        )}
        {!l.phone && !l.email && !websiteHref && (
          <span style={{ color: theme.textMute, fontSize: '0.8rem' }}>No contact info yet</span>
        )}
      </div>

      {contact && (
        <div style={{ color: theme.textMute, fontSize: '0.78rem', marginTop: '8px' }}>Contact: {contact}</div>
      )}

      {/* Action row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px' }}>
        {!l.assignment_id ? (
          <button onClick={(e) => { e.stopPropagation(); onAssign(l.id); }}
            style={{ ...btn(theme.primary, '#fff'), flex: 1, padding: '11px', fontSize: '0.9rem' }}>
            + Add to my pipeline
          </button>
        ) : (
          <>
            <StatusBadge status={l.assignment_status} />
            <button onClick={onToggle} style={{ ...btn(theme.surface, theme.text), padding: '9px 14px', marginLeft: 'auto' }}>
              {expanded ? 'Close' : 'Update'}
            </button>
          </>
        )}
      </div>

      {expanded && (
        <div style={{ marginTop: '14px' }}>
          <LeadDrawer lead={l} detail={detail} timeline={timeline}
            onAssign={onAssign} onStatus={onStatus}
            onSaveNotes={onSaveNotes} onFollowUp={onFollowUp} />
        </div>
      )}
    </div>
  );
};

// Removable pill for an applied filter — the query builder stays legible
// as a row of values rather than state hidden inside the dropdowns.
const FilterPill = ({ label, onRemove }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '5px 11px',
    borderRadius: '14px', fontSize: '0.78rem', color: theme.heading,
    backgroundColor: theme.surfaceAlt, border: `1px solid ${theme.border}`,
  }}>
    {label}
    <button onClick={onRemove} aria-label={`Remove ${label} filter`} style={{
      background: 'none', border: 'none', color: theme.textMute, cursor: 'pointer',
      padding: 0, fontSize: '0.8rem', lineHeight: 1,
    }}>✕</button>
  </span>
);

// ─────────────────────────────────────────────────────────────────────────
// Record drawer — the provenance view. Every value is shown with where it
// came from, how confident the provider was, when it was last checked, and
// what it said before. Apollo shows a phone number; this shows why to
// trust it.
// ─────────────────────────────────────────────────────────────────────────
const PROVIDER_LABELS = {
  gemini: 'Gemini (AI, web-grounded)',
  openai: 'OpenAI (AI, web-grounded)',
  twilio_lookup: 'Twilio phone lookup',
  web_check: 'automated website check',
  self_reported: 'reported by the business itself',
  source: 'straight from the source record',
  derived: 'derived from public records',
  manual: 'entered by hand',
};
const FIELD_LABELS = {
  website: 'Website', phone: 'Phone', email: 'Email',
  registered_agent: 'Registered agent', dba_name: 'Doing business as',
  owner: 'Owner', county: 'County', city: 'City',
  lead_signal: 'Lead signal', restaurant_classification: 'Classification',
};
// Contact fields first; anything else the trail contains follows in stored
// order. phone_line_type folds into the Phone row instead of its own.
const FIELD_ORDER = ['website', 'phone', 'email', 'registered_agent', 'dba_name'];
const HIDDEN_FIELDS = new Set(['phone_line_type']);

const fmtDay = (d) => (d ? new Date(d).toLocaleDateString() : null);
const pct = (c) => (c === null || c === undefined ? null : `${Math.round(c * 100)}%`);

// Citations are stored as-is (list of URLs, or a provider-specific object).
// Render only the parts that are links; the provider label covers the rest.
const citationLinks = (citations) => {
  const raw = Array.isArray(citations) ? citations
    : citations && typeof citations === 'object' ? Object.values(citations)
    : citations ? [citations] : [];
  return raw
    .map((c) => (typeof c === 'object' && c !== null ? c.url || c.uri : c))
    .filter((u) => typeof u === 'string' && u.startsWith('http'))
    .map((u) => {
      try { return { url: u, host: new URL(u).hostname.replace(/^www\./, '') }; }
      catch { return null; }
    })
    .filter(Boolean);
};

const drawerLabel = {
  display: 'block', color: theme.textMute, fontSize: '0.72rem',
  marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600,
};

// One field's trail: current value, its provenance line, sources it was
// grounded on, and what the value was before.
const FieldTrail = ({ field, items }) => {
  const [current, ...history] = items;
  const links = citationLinks(current.citations);
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ color: theme.textMute, fontSize: '0.78rem', minWidth: '110px' }}>
          {FIELD_LABELS[field] || field.replace(/_/g, ' ')}
        </span>
        <span style={{ color: theme.heading, fontSize: '0.88rem', fontWeight: 600, overflowWrap: 'anywhere' }}>
          {current.value || '—'}
        </span>
      </div>
      <div style={{ color: theme.textMute, fontSize: '0.75rem', marginTop: '3px', marginLeft: '118px' }}>
        {PROVIDER_LABELS[current.provider] || current.provider}
        {pct(current.confidence) && ` · ${pct(current.confidence)} confident`}
        {fmtDay(current.created_at) && ` · checked ${fmtDay(current.created_at)}`}
      </div>
      {links.length > 0 && (
        <div style={{ color: theme.textMute, fontSize: '0.75rem', marginTop: '2px', marginLeft: '118px' }}>
          {'grounded on: '}
          {links.map((c, i) => (
            <React.Fragment key={c.url}>
              {i > 0 && ', '}
              <a href={c.url} target="_blank" rel="noreferrer" style={{ color: theme.primary, textDecoration: 'none' }}>{c.host}</a>
            </React.Fragment>
          ))}
        </div>
      )}
      {history.length > 0 && (
        <div style={{ color: theme.textMute, fontSize: '0.75rem', marginTop: '2px', marginLeft: '118px' }}>
          {'previously: '}
          {history.slice(0, 3).map((h, i) => (
            <span key={i}>
              {i > 0 && '; '}
              {h.value || '(empty)'} ({PROVIDER_LABELS[h.provider] || h.provider}, {fmtDay(h.created_at)})
            </span>
          ))}
          {history.length > 3 && ` — and ${history.length - 3} older`}
        </div>
      )}
    </div>
  );
};

// Do-not-contact state, on the record itself rather than buried at the
// export boundary. Wording is deliberately plain — this is a compliance
// signal, not decoration.
const DNC_SOURCE_TEXT = {
  opt_out: 'this business asked to be removed',
  dnc_import: 'the number is on an imported do-not-call list',
  manual: 'it was added to your suppression list by hand',
};
const DncBanner = ({ dnc, lineType }) => {
  if (!dnc || !dnc.checked) return null;
  if (dnc.suppressed) {
    return (
      <div style={{
        backgroundColor: '#3A1220', border: '1px solid #F87171', borderRadius: '8px',
        padding: '10px 14px', color: '#FCA5A5', fontSize: '0.85rem', fontWeight: 600,
      }}>
        ⛔ Do not contact — {DNC_SOURCE_TEXT[dnc.source] || 'this contact is on your suppression list'}
        {dnc.reason ? ` (${dnc.reason})` : ''}. Exports already leave it out.
      </div>
    );
  }
  const cellCaution = lineType === 'mobile' || lineType === 'voip';
  return (
    <div style={{
      backgroundColor: '#0C3428', border: '1px solid #10B98155', borderRadius: '8px',
      padding: '9px 14px', color: '#6EE7B7', fontSize: '0.82rem',
    }}>
      ✓ Not on your do-not-contact list
      {cellCaution && ' · this is a mobile/VoIP number — no autodialing or texting without consent (TCPA)'}
    </div>
  );
};

// One activity event as a plain-English line.
const eventLine = (ev) => {
  switch (ev.event_type) {
    case 'assigned':
      return ev.detail && ev.detail.bulk ? 'added to pipeline (bulk add)' : 'added to pipeline';
    case 'status_change':
      return `status: ${(ev.from_status || '—').replace('_', ' ')} → ${(ev.to_status || '—').replace('_', ' ')}`;
    case 'note': {
      const n = ev.note || '';
      return `note: “${n.slice(0, 80)}${n.length > 80 ? '…' : ''}”`;
    }
    case 'follow_up':
      return ev.detail && ev.detail.next_action_at
        ? `follow-up set for ${fmtDay(ev.detail.next_action_at)}`
        : 'follow-up cleared';
    default:
      return ev.event_type.replace(/_/g, ' ');
  }
};

const LeadDrawer = ({ lead: l, detail, timeline, onAssign, onStatus, onSaveNotes, onFollowUp }) => {
  const data = detail && detail.data;
  const fields = data ? data.fields : {};
  const orderedFields = [
    ...FIELD_ORDER.filter((f) => fields[f] && fields[f].length),
    ...Object.keys(fields).filter((f) => !FIELD_ORDER.includes(f) && !HIDDEN_FIELDS.has(f)),
  ];
  return (
    <div style={{ display: 'grid', gap: '14px' }}>
      {data && <DncBanner dnc={data.dnc} lineType={l.line_type} />}
      {/* Why this score: the badge plus its reasons with their points, so
          the number argues for itself instead of being a verdict. */}
      {l.lead_score !== null && l.lead_score !== undefined && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <ScoreBadge score={l.lead_score} />
          <span style={{ color: theme.textMute, fontSize: '0.78rem' }}>match score:</span>
          {scoreReasons(l).map((c) => (
            <span key={c.label} style={{
              padding: '3px 9px', borderRadius: '12px', fontSize: '0.75rem',
              color: theme.heading, backgroundColor: theme.surfaceAlt,
              border: `1px solid ${theme.border}`,
            }}>
              {c.label}{' '}
              <span style={{ color: c.pts < 0 ? theme.danger : theme.accent, fontWeight: 600 }}>
                {c.pts > 0 ? `+${c.pts}` : c.pts}
              </span>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* Provenance: the append-only enrichment trail per field. */}
        <div style={{ flex: '2 1 340px', minWidth: '280px' }}>
          <label style={drawerLabel}>Where this data came from</label>
          {detail && detail.loading && (
            <div style={{ color: theme.textMute, fontSize: '0.85rem' }}>Loading record history…</div>
          )}
          {detail && detail.error && (
            <div style={{ color: theme.textMute, fontSize: '0.85rem' }}>
              Record history isn't available right now.
            </div>
          )}
          {data && orderedFields.length === 0 && (
            <div style={{ color: theme.textMute, fontSize: '0.85rem' }}>
              Nothing enriched yet — this record is fresh from the source feeds.
            </div>
          )}
          {data && orderedFields.map((f) => <FieldTrail key={f} field={f} items={fields[f]} />)}
        </div>
        {/* Identity + this user's pipeline controls. */}
        <div style={{ flex: '1 1 260px', minWidth: '240px', display: 'grid', gap: '16px', alignContent: 'start' }}>
          {data && (
            <div>
              <label style={drawerLabel}>
                Identity — from {data.sources.length || 'the'} source record{data.sources.length === 1 ? '' : 's'}
              </label>
              {data.sources.length === 0 && (
                <div style={{ color: theme.textMute, fontSize: '0.8rem' }}>No merge history recorded.</div>
              )}
              {data.sources.map((s, i) => (
                <div key={i} style={{ marginBottom: '8px' }}>
                  <div style={{ color: theme.heading, fontSize: '0.82rem', fontWeight: 600 }}>
                    {s.label}
                    {pct(s.match_confidence) && (
                      <span style={{ color: theme.textMute, fontWeight: 400 }}> · match {pct(s.match_confidence)}</span>
                    )}
                  </div>
                  <div style={{ color: theme.textMute, fontSize: '0.75rem' }}>
                    {[
                      s.detail && s.detail.license_status && `status: ${s.detail.license_status}`,
                      s.detail && s.detail.filing_date && `filed ${fmtDay(s.detail.filing_date)}`,
                      s.detail && s.detail.submitted && `submitted ${fmtDay(s.detail.submitted)}`,
                      s.detail && s.detail.consented && 'consented to contact',
                      fmtDay(s.merged_at) && `merged ${fmtDay(s.merged_at)}`,
                    ].filter(Boolean).join(' · ')}
                  </div>
                </div>
              ))}
              {data.address && (
                <div style={{ color: theme.textMute, fontSize: '0.78rem', marginTop: '6px' }}>
                  {[data.address, data.city, data.zip].filter(Boolean).join(', ')}
                </div>
              )}
              {CHAIN_TYPES.has(l.operator_type) && (
                <div style={{ color: '#FBBF24', fontSize: '0.78rem', marginTop: '6px' }}>
                  {l.operator_type === 'national_brand' && l.chain_brand
                    ? `Part of ${l.chain_brand} — brand locations usually get their website from corporate.`
                    : l.operator_type === 'multi_unit'
                      ? 'Numbered location — likely one unit of several.'
                      : 'Franchised location.'}
                </div>
              )}
            </div>
          )}
          <div>
            <label style={drawerLabel}>My pipeline</label>
            {l.assignment_id ? (
              <div style={{ display: 'grid', gap: '10px' }}>
                <select value={l.assignment_status || 'new'} onChange={(e) => onStatus(l, e.target.value)} style={{
                  padding: '9px 12px', borderRadius: '6px', border: `1px solid ${theme.border}`,
                  backgroundColor: theme.surfaceAlt, color: theme.heading, fontSize: '0.85rem',
                }}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
                <NotesInput lead={l} onSave={onSaveNotes} />
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px',
                  color: theme.textMute, fontSize: '0.78rem' }}>
                  Follow up:
                  <input type="date"
                    value={(l.assignment_next_action || '').slice(0, 10)}
                    onChange={(e) => onFollowUp(l, e.target.value)}
                    style={{ padding: '7px 10px', borderRadius: '6px', border: `1px solid ${theme.border}`,
                      backgroundColor: theme.surfaceAlt, color: theme.heading, fontSize: '0.82rem' }} />
                  {l.assignment_next_action && (
                    <button onClick={() => onFollowUp(l, '')} style={{
                      background: 'none', border: 'none', color: theme.textMute,
                      cursor: 'pointer', padding: 0, fontSize: '0.78rem', textDecoration: 'underline',
                    }}>clear</button>
                  )}
                </label>
              </div>
            ) : (
              <button onClick={() => onAssign(l.id)}
                style={{ ...btn(theme.primary, '#fff'), padding: '9px 16px', fontSize: '0.85rem' }}>
                + Add to my pipeline
              </button>
            )}
            {l.website && (
              <a href={l.website.startsWith('http') ? l.website : `https://${l.website}`} target="_blank" rel="noreferrer"
                style={{ display: 'inline-block', color: theme.primary, textDecoration: 'none', fontSize: '0.82rem', marginTop: '10px' }}>
                Visit website ↗
              </a>
            )}
          </div>
          {/* Append-only activity timeline — every take, status change, note
              and follow-up, newest first. History never overwrites. */}
          {l.assignment_id && (
            <div>
              <label style={drawerLabel}>Activity</label>
              {timeline && timeline.loading && (
                <div style={{ color: theme.textMute, fontSize: '0.8rem' }}>Loading activity…</div>
              )}
              {timeline && timeline.data && timeline.data.length === 0 && (
                <div style={{ color: theme.textMute, fontSize: '0.8rem' }}>
                  Nothing recorded yet — changes from before the activity log existed weren't tracked.
                </div>
              )}
              {timeline && timeline.data && timeline.data.slice(0, 8).map((ev, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '4px',
                  color: theme.text, fontSize: '0.78rem' }}>
                  <span style={{ color: theme.textMute, flexShrink: 0, minWidth: '64px' }}>
                    {fmtDay(ev.created_at)}
                  </span>
                  <span>{eventLine(ev)}</span>
                </div>
              ))}
              {timeline && timeline.data && timeline.data.length > 8 && (
                <div style={{ color: theme.textMute, fontSize: '0.75rem' }}>
                  … and {timeline.data.length - 8} older
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const contactChip = (color) => ({
  display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '9px 13px',
  borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none',
  color, backgroundColor: theme.surfaceAlt, border: `1px solid ${theme.border}`,
});

// Florida lets a new entity elect an effective date up to 90 days after
// filing, so a future filing date means "this business opens then" — a
// pre-opening lead, not a data error. (The API drops anything further out
// as a source typo.)
const isUpcoming = (dateStr) => Boolean(dateStr) && new Date(dateStr).getTime() > Date.now();

const UpcomingBadge = () => (
  <span style={{
    marginLeft: '8px', padding: '2px 7px', borderRadius: '10px',
    fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.5px',
    textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0,
    color: '#FBBF24', backgroundColor: '#3A2E12',
  }}>Starts soon</span>
);

// Score badge — colored by strength. Null (unscored) renders as a muted
// dash: an unscored record says so rather than showing a fake number.
const scoreColors = (n) =>
  n >= 70 ? { fg: '#10B981', bg: '#0C3428' }
  : n >= 40 ? { fg: '#FBBF24', bg: '#3A2E12' }
  : { fg: theme.textMute, bg: theme.surfaceAlt };
const ScoreBadge = ({ score }) => {
  if (score === null || score === undefined) {
    return <span style={{ color: theme.textMute, fontSize: '0.8rem' }}>—</span>;
  }
  const c = scoreColors(score);
  return (
    <span style={{
      display: 'inline-block', minWidth: '30px', textAlign: 'center',
      padding: '3px 8px', borderRadius: '10px', fontSize: '0.8rem', fontWeight: 700,
      color: c.fg, backgroundColor: c.bg,
    }}>{score}</span>
  );
};

// Reason chips: why the score is what it is, in plain English. Derived from
// the breakdown components plus row data the table already has, so a wrong
// score is debuggable at a glance and never needs a support email.
const scoreReasons = (l) => {
  const b = l.score_breakdown;
  if (!b) return [];
  const chips = [];
  if (b.recency > 0) {
    if (isUpcoming(l.filing_date)) {
      chips.push({ label: 'starts soon', pts: b.recency });
    } else if (l.filing_date) {
      const days = Math.max(0, Math.round((Date.now() - new Date(l.filing_date).getTime()) / 86400000));
      chips.push({ label: days === 0 ? 'filed today' : `filed ${days} day${days !== 1 ? 's' : ''} ago`, pts: b.recency });
    }
  }
  if (b.digital_gap > 0) chips.push({ label: 'no website yet', pts: b.digital_gap });
  if (b.reachability > 0) {
    chips.push({
      label: l.line_type === 'landline' ? 'landline phone'
        : l.phone ? 'has a phone number' : 'email only',
      pts: b.reachability,
    });
  }
  if (b.signal > 0 && l.lead_signal && SIGNAL_BADGES[l.lead_signal]) {
    chips.push({ label: SIGNAL_BADGES[l.lead_signal].label.toLowerCase(), pts: b.signal });
  }
  if (b.chain < 0) {
    chips.push({
      label: l.chain_brand ? `part of ${l.chain_brand}` : 'part of a chain',
      pts: b.chain,
    });
  }
  return chips;
};

// Muted tag on rows the classifier identified as chain operators. Absence
// means unclassified, not verified independent — so no tag for NULL.
const CHAIN_TYPES = new Set(['national_brand', 'franchisee', 'corporate_store', 'small_chain', 'multi_unit']);
const ChainTag = ({ lead: l }) => {
  if (!CHAIN_TYPES.has(l.operator_type)) return null;
  return (
    <span title={l.chain_brand || 'numbered or franchised location'} style={{
      marginLeft: '8px', padding: '2px 7px', borderRadius: '10px',
      fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.5px',
      textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0,
      color: theme.textMute, backgroundColor: theme.surfaceAlt,
      border: `1px solid ${theme.border}`,
    }}>chain</span>
  );
};

// Line-type tag next to a phone. Landline reads as safe (green); mobile/voip
// carry TCPA autodial/text restrictions (amber caution); unknown is muted.
const LINE_TYPE_STYLES = {
  mobile:   { label: 'mobile',   fg: '#F59E0B', bg: '#3A2E12' },
  voip:     { label: 'voip',     fg: '#F59E0B', bg: '#3A2E12' },
  landline: { label: 'landline', fg: '#10B981', bg: '#0C3428' },
};
const LineTypeTag = ({ type }) => {
  const s = LINE_TYPE_STYLES[type];
  if (!s) return null;
  return (
    <span style={{
      marginLeft: '6px', padding: '1px 6px', borderRadius: '8px', fontSize: '0.6rem',
      fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px',
      color: s.fg, backgroundColor: s.bg, whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
};

// Status badge — colored by status
const StatusBadge = ({ status }) => {
  const colors = statusTheme[status] || statusTheme.new;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: '12px', fontSize: '0.72rem', fontWeight: 600,
      textTransform: 'capitalize', color: colors.fg, backgroundColor: colors.bg,
      border: `1px solid ${colors.fg}33`,
    }}>{(status || 'new').replace('_', ' ')}</span>
  );
};

// Debounced notes input — saves on blur
const NotesInput = ({ lead, onSave }) => {
  const [val, setVal] = useState(lead.assignment_notes || '');
  useEffect(() => { setVal(lead.assignment_notes || ''); }, [lead.assignment_notes]);
  return (
    <input value={val} onChange={(e) => setVal(e.target.value)}
      onBlur={() => val !== (lead.assignment_notes || '') && onSave(lead, val)}
      placeholder="Add notes…"
      style={{
        width: '100%', padding: '8px 12px', borderRadius: '6px', border: `1px solid ${theme.border}`,
        backgroundColor: theme.surfaceAlt, color: theme.heading, fontSize: '0.85rem',
      }} />
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Account / Billing screen (Milestone 4)
// ─────────────────────────────────────────────────────────────────────────
const PLANS = [
  { tier: 'free', name: 'Free', quota: 50, price: '$0', features: ['50 leads / month', 'CSV export', 'Basic filters'] },
  { tier: 'pro', name: 'Pro', quota: 500, price: '$49/mo', features: ['500 leads / month', 'All export formats', 'Autodialer export', 'Notes & status tracking'], featured: true },
  { tier: 'enterprise', name: 'Enterprise', quota: 5000, price: 'Custom', features: ['5,000 leads / month', 'Priority ingestion', 'Team features', 'API access'] },
];

const AccountScreen = ({ quota, onClose, onCheckout, onPortal }) => {
  const currentTier = quota.max >= 5000 ? 'enterprise' : quota.max >= 500 ? 'pro' : 'free';
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        backgroundColor: theme.bg, borderRadius: '12px', border: `1px solid ${theme.border}`,
        maxWidth: '760px', width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: '32px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ color: theme.heading, margin: 0, fontSize: '1.3rem' }}>Account & Billing</h2>
          <button onClick={onClose} style={{ ...btn(theme.surface, theme.text), padding: '6px 14px' }}>✕</button>
        </div>

        {/* Current usage */}
        <div style={{ backgroundColor: theme.surface, borderRadius: '8px', padding: '16px', marginBottom: '24px', border: `1px solid ${theme.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: theme.textMute, fontSize: '0.85rem' }}>Current plan: <strong style={{ color: theme.heading, textTransform: 'capitalize' }}>{currentTier}</strong></span>
            <span style={{ color: theme.textMute, fontSize: '0.85rem' }}>{quota.used} / {quota.max} leads used</span>
          </div>
          <div style={{ height: '6px', backgroundColor: theme.surfaceAlt, borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(100, (quota.used / quota.max) * 100)}%`, height: '100%', backgroundColor: theme.primary }} />
          </div>
        </div>

        {/* Plans */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          {PLANS.map((p) => (
            <div key={p.tier} style={{
              backgroundColor: theme.surface, borderRadius: '10px', padding: '20px',
              border: `1px solid ${p.featured ? theme.primary : theme.border}`,
              position: 'relative',
            }}>
              {p.featured && <div style={{
                position: 'absolute', top: '-10px', right: '16px', backgroundColor: theme.primary,
                color: '#fff', fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: '10px',
              }}>POPULAR</div>}
              <h3 style={{ color: theme.heading, margin: '0 0 4px', fontSize: '1.1rem' }}>{p.name}</h3>
              <div style={{ color: theme.primary, fontSize: '1.4rem', fontWeight: 700, marginBottom: '12px' }}>{p.price}</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px' }}>
                {p.features.map((f) => (
                  <li key={f} style={{ color: theme.text, fontSize: '0.8rem', marginBottom: '6px' }}>✓ {f}</li>
                ))}
              </ul>
              {p.tier === currentTier ? (
                <div style={{ ...btn(theme.surfaceAlt, theme.textMute), textAlign: 'center', cursor: 'default' }}>Current plan</div>
              ) : p.tier === 'free' ? (
                <div style={{ ...btn(theme.surfaceAlt, theme.textMute), textAlign: 'center', cursor: 'default' }}>Base tier</div>
              ) : (
                <button onClick={() => onCheckout(p.tier)} style={{ ...btn(theme.primary, '#fff'), width: '100%' }}>
                  Upgrade to {p.name}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Manage billing */}
        {currentTier !== 'free' && (
          <button onClick={onPortal} style={{ ...btn(theme.surface, theme.primary), width: '100%' }}>
            Manage billing via Stripe →
          </button>
        )}
        <p style={{ color: theme.textMute, fontSize: '0.75rem', textAlign: 'center', marginTop: '16px' }}>
          Plans and quotas are managed by Stripe. Cancel anytime.
        </p>
      </div>
    </div>
  );
};

// Export dropdown item
const ExportItem = ({ label, hint, onClick }) => (
  <div onClick={onClick} style={{
    padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${theme.border}`,
  }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.surfaceAlt)}
    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}>
    <div style={{ color: theme.heading, fontSize: '0.85rem', fontWeight: 500 }}>{label}</div>
    <div style={{ color: theme.textMute, fontSize: '0.72rem', marginTop: '2px' }}>{hint}</div>
  </div>
);

// Style helpers
const btn = (bg, fg) => ({
  padding: '9px 18px', backgroundColor: bg, color: fg, border: `1px solid ${theme.border}`,
  borderRadius: '6px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem',
});
const cell = (color = theme.text, bold = false) => ({
  padding: '10px 12px', color, fontWeight: bold ? 600 : 400, fontSize: '0.85rem',
});

// ─────────────────────────────────────────────────────────────────────────
// Root
// ─────────────────────────────────────────────────────────────────────────
export default function App() {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const handleLogin = (t) => { localStorage.setItem('token', t); setToken(t); };
  const handleLogout = () => { localStorage.removeItem('token'); setToken(null); };
  const dashboard = token
    ? <Dashboard token={token} onLogout={handleLogout} />
    : <AuthScreen onLoginSuccess={handleLogin} />;
  return (
    <BrowserRouter>
      <RouteAnalytics />
      <Routes>
        {/* Marketing front page. */}
        <Route path="/" element={<LandingPage isAuthed={!!token} />} />
        {/* Digital-PR report (public). */}
        <Route path="/florida-new-business-report" element={<MonthlyReport />} />
        <Route path="/report" element={<MonthlyReport />} />
        {/* Legal. */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/guide" element={<GuidePage />} />
        {/* Public SEO pages — no auth. */}
        <Route path="/new/:profession/:city" element={<PublicListings />} />
        <Route path="/new/:profession" element={<PublicListings />} />
        <Route path="/new" element={<PublicListings />} />
        {/* The subscriber app: dashboard when signed in, else the auth screen. */}
        <Route path="/app" element={dashboard} />
        {/* Unknown paths fall back to the front page. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
